// OFF-03 — Realtime integrity (docs/architecture/27-offline-realtime-and-resilience.md).
//
// Resolves defect C4 / P1: a dropped realtime channel that silently stops
// delivering must NOT leave a board that looks live but is stale. R27.8 requires
// every subscription to carry a heartbeat; R27.9 requires a freshness indicator
// on any realtime-fed surface.
//
// The Supabase RealtimeClient already emits heartbeat lifecycle events
// (`client.realtime.onHeartbeat`). We record them centrally and expose a
// connection-liveness snapshot plus a per-surface "last data sync" timestamp.
// Hooks call markRealtimeSync() on every delivered change; surfaces call
// useRealtimeFreshness() to decide when to show the stale indicator.

import { reportOpSignal } from '@/lib/client/observability/op-signal';

/**
 * Lifecycle status reported by the Supabase Realtime heartbeat. `@supabase/realtime-js`
 * defines this union on `RealtimeClient` but does not re-export it from the package
 * root, so we mirror the same set of values here. Keep the two in sync.
 */
export type HeartbeatStatus = 'sent' | 'ok' | 'error' | 'timeout' | 'disconnected';

export const REALTIME_HEARTBEAT_INTERVAL_MS = 30_000;
/** No heartbeat within this window ⇒ the connection is presumed dead (R27.8). */
export const REALTIME_DEAD_AFTER_MS = 90_000;
/** A realtime-fed surface older than this shows the freshness indicator (R27.9). */
export const REALTIME_FRESHNESS_THRESHOLD_MS = 120_000;

export type RealtimeHealthSnapshot = {
  /** Latest heartbeat status, or null before the first heartbeat. */
  status: HeartbeatStatus | null;
  lastHeartbeatAt: number | null;
  /** A heartbeat has arrived within REALTIME_DEAD_AFTER_MS. */
  alive: boolean;
  /** Last time a real change was delivered or a refetch succeeded. */
  lastSyncAt: number | null;
};

type HealthListener = (snapshot: RealtimeHealthSnapshot) => void;

let lastHeartbeatAt: number | null = null;
let lastStatus: HeartbeatStatus | null = null;
let lastSyncAt: number | null = null;
const listeners = new Set<HealthListener>();
let watchdog: ReturnType<typeof setInterval> | null = null;

function snapshot(): RealtimeHealthSnapshot {
  const alive =
    lastHeartbeatAt != null && Date.now() - lastHeartbeatAt <= REALTIME_DEAD_AFTER_MS;
  return { status: lastStatus, lastHeartbeatAt, alive, lastSyncAt };
}

function emit() {
  const snap = snapshot();
  for (const listener of listeners) listener(snap);
}

function ensureWatchdog() {
  if (watchdog == null && typeof window !== 'undefined') {
    // Re-emit periodically so `alive` and staleness recompute as time passes.
    watchdog = setInterval(emit, 5_000);
  }
}

function stopWatchdog() {
  if (watchdog != null) {
    clearInterval(watchdog);
    watchdog = null;
  }
}

/** Called from lib/realtime.ts on every RealtimeClient heartbeat. */
export function recordHeartbeat(status: HeartbeatStatus): void {
  const previous = lastStatus;
  lastHeartbeatAt = Date.now();
  lastStatus = status;
  // A successful heartbeat confirms the socket is live; treat it as liveness.
  if (status === 'ok' || status === 'sent') markRealtimeSync();
  // Report the TRANSITION into a dead channel, not every failing heartbeat, so
  // a flapping connection is one op signal per outage rather than a flood.
  // The server sees nothing of this; it is a client-reported ops metric
  // (fastfood-workflow plan Phase 3 item 4, `ops:realtime-disconnect`).
  else if (!isDeadStatus(previous)) {
    void reportOpSignal({ signal: 'realtime_disconnect' });
  }
  emit();
}

/** A heartbeat status that means the channel is no longer delivering. */
function isDeadStatus(status: HeartbeatStatus | null): boolean {
  return status === 'error' || status === 'timeout' || status === 'disconnected';
}

/** Call whenever a real change is delivered or a refetch succeeds. */
export function markRealtimeSync(): void {
  lastSyncAt = Date.now();
  emit();
}

export function getRealtimeHealth(): RealtimeHealthSnapshot {
  return snapshot();
}

/** Subscribe to health changes; returns an unsubscribe function. */
export function subscribeRealtimeHealth(listener: HealthListener): () => void {
  listeners.add(listener);
  listener(snapshot());
  ensureWatchdog();
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) stopWatchdog();
  };
}
